/**
 * 最低賃金の警告について「**何を出すか**」だけを決める（M5b / M5e）。
 *
 * ## ★なぜ DOM から切り離すか
 *
 * M5b で、警告の取得に失敗したとき `undefined` を返し、
 * `renderShell` が `undefined`（＝出さない相手）と同じに扱ったため
 * **警告欄が画面から丸ごと消えた**（spec-reviewer 差し戻し）。
 * `"unavailable"` を足して直したが、当時は `src/ui/` に検査基盤が無く、
 * **直した分岐を元へ戻しても `tsc` もテストも落ちない**状態のまま完了していた
 * （`.loop/memory.md`「検査基盤が無い層の修正は、『画面で見た』までしか言えない」）。
 * ★2026-08-25 / M6c：`minimumWageBanner` が5つの kind すべてを DOM へ写すことを
 * `tests/ui/main-dom.test.ts` が固定した。分岐を1つ飛ばす変異で落ちる。
 *
 * ★このファイルは **DOM を触らない。** `document` も `el()` も使わない。
 *   そのため node 環境のまま `tests/ui/` から検査できる（jsdom を読み込まない）。
 *   **判断をここだけに置く**——描画側に「出す／出さない」を1つでも残すと、
 *   残した側が無検査になる（`.loop/memory.md`「判定を各所に書くと、
 *   書き忘れた場所が『制限なし』になる」）。
 *
 * ★氏名も個人の時給も出さない（絶対ルール1）。出すのは**日付と人数と最低賃金**だけ。
 */
import type { MinimumWageAlert } from "./api.js";

/**
 * 画面上部の警告に渡す状態。
 * ★`"unavailable"` は**取得できなかった**という意味。`undefined`（＝出さない相手）と別物。
 */
export type WageAlertState = { alert: MinimumWageAlert; truncated: boolean } | "unavailable";

/**
 * 画面に出す要素1つ。描画側はこれを DOM へ写すだけで、出す／出さないを決めない。
 *
 * - `unavailable` — 取得できなかった（**「該当なし」ではない**）
 * - `failure` — 下回る人がいる。`date` はその日付（判定画面への導線に使う）。
 *   `summary` が `null` でなければ**畳んで**出す（AC-8）
 * - `skipped` — 判定できなかった日
 * - `passed` — 該当なし。**沈黙させない**ために出す
 * - `truncated` — 未来の改定が多すぎて途中までしか判定していない
 */
export type WageAlertBlock =
  | { kind: "unavailable"; text: string }
  | { kind: "failure"; text: string; date: string; summary: string | null }
  | { kind: "skipped"; text: string }
  | { kind: "passed"; text: string }
  | { kind: "truncated"; text: string };

/**
 * ★2回目以降に出す畳んだ見出しの文言。
 *
 * **人数も最低賃金も入れない。** 店舗端末は共有で、staff / leader も同じ画面の前に立つ。
 * 「◯名が◯円を下回る」は**この規模の店舗では個人の推測に近づく**（絶対ルール1）。
 * 畳む目的は肩越しに読まれる面積を減らすことなので、
 * **要点だけ残しても数字が残れば意味が無い。**
 * ★ここに人数を書かない（コメントであっても）。**推測の数字が残ると根拠として読まれる。**
 *
 * ★それでも**赤い1行としては残す**（描画側が `.notice error` を当てる）。
 *   畳むのは読ませないためで、気づかせないためではない。
 */
const foldedSummary = (date: string) => `最低賃金の対応が必要です（${date}）`;

/** ★全文を読む場所（判定と改定の入口があるタブ）。ここ以外は1本・1行 */
const FULL_TABS: readonly string[] = ["settings", "staff"];

/**
 * 設定・従業員タブ以外で出す1本（下回る人がいないとき）。
 * 優先は 判定できない日 ＞ 途中までの判定 ＞ 該当なし。★該当なしでも1行は残す（沈黙させない）
 */
function compactBlock(
  alert: Exclude<MinimumWageAlert, { status: "failed" }>,
  truncated: boolean,
): WageAlertBlock {
  if (alert.status === "skipped") {
    return {
      kind: "skipped",
      text: "最低賃金：判定できない日あり（設定タブ）",
    };
  }
  if (truncated) {
    return { kind: "truncated", text: "最低賃金：判定が途中までです（設定タブ）" };
  }
  return { kind: "passed", text: "最低賃金：該当なし" };
}

/**
 * 状態から、画面に出す要素の並びを作る。
 *
 * @param state `undefined` は「出さない相手」（staff / leader）。担保はサーバの 403
 * @param opts.shownFull 人数入りの全面表示を、このページで**もう出したか**（AC-8）
 * @param opts.tab いま開いているタブ。設定・従業員タブ以外では**1本・1行**に畳む（`docs/10` §5）。
 *   ★必須にしてある——省けると、呼び出し側が渡し忘れても全文のまま黙って動く
 */
export function wageAlertBlocks(
  state: WageAlertState | undefined,
  opts: { shownFull: boolean; tab: string },
): WageAlertBlock[] {
  if (state === undefined) return [];
  const compact = !FULL_TABS.includes(opts.tab);

  if (state === "unavailable") {
    // ★1行にしても黙らない（下の理由はそのまま）
    if (compact) {
      return [{ kind: "unavailable", text: "最低賃金：確認できません（設定タブ）" }];
    }
    /*
     * ★**沈黙で返さない。** ここが `undefined` と同じ扱いだったとき、
     *   警告欄が画面から丸ごと消え、店長には「該当なし」と区別が付かなかった。
     *   `console.error` は開発者にしか届かない——この案件は保守契約が無く、
     *   コンソールを見る人がそもそもいない（2026-08-18・spec-reviewer 差し戻し）。
     *
     * ★**気づく仕組みが壊れたことにも気づけること。** それがこの課題の intent。
     */
    return [
      {
        kind: "unavailable",
        text:
          "最低賃金の警告を確認できませんでした。下回っている従業員がいるかどうかは不明です。" +
          "設定タブか従業員タブで判定を開いて確認してください",
      },
    ];
  }

  const { alert, truncated } = state;
  const out: WageAlertBlock[] = [];

  if (alert.status === "failed") {
    const { date, count, minimumWage } = alert.firstFailure;
    const today = alert.checkedDates[0];
    out.push({
      kind: "failure",
      text:
        date === today
          ? `本日（${date}）、${count}名 の時給が最低賃金 ${minimumWage.toLocaleString()}円 を下回っています`
          : `${date} から、${count}名 の時給が最低賃金 ${minimumWage.toLocaleString()}円 を下回ります`,
      date,
      // ★毎日使うタブでは初回から畳む（共有端末で肩越しに読まれる面積も減る）
      summary: compact || opts.shownFull ? foldedSummary(date) : null,
    });
    if (compact) return out;
  } else if (compact) {
    return [compactBlock(alert, truncated)];
  }

  /*
   * ★`failed` のときも出す。片方に畳むと「10月は10名。ただし11月は判定できていない」が
   *   「10月は10名」になる（AC-2）。
   */
  if (alert.status !== "passed") {
    for (const s of alert.skipped) {
      out.push({ kind: "skipped", text: `${s.date} は判定できません：${s.reason}` });
    }
  }

  if (alert.status === "passed") {
    /*
     * ★沈黙させない。**判定した件数と日付を出す。**
     *   何も出さないと「まだ調べていない」と「調べて該当なし」が同じ見た目になる
     *   （`.loop/memory.md`「『N件すべて緑』を受け取る前に、走った件数と…」）。
     */
    out.push({
      kind: "passed",
      text:
        `最低賃金：${alert.checkedDates.join(" / ")} を判定。該当なし` +
        /*
         * ★複数日あるとき `checked` は**いちばん人数の少ない日**の件数
         *   （`summarizeMinimumWageAlert` が安全側に倒している）。
         *   「全体で N名 しかいない」と読ませない。
         */
        (alert.checkedDates.length > 1
          ? `（いちばん人数の少ない日で ${alert.checked}名）`
          : `（${alert.checked}名）`),
    });
  }

  if (truncated) {
    out.push({
      kind: "truncated",
      text:
        "未来の最低賃金の改定が多すぎるため、途中までしか判定していません。" +
        "設定タブで改定の登録を確認してください",
    });
  }

  return out;
}

/**
 * この並びに、**人数入りの全面表示**が含まれるか（＝次からは畳む）。
 *
 * ★描画側に `if (畳んでいない) フラグを立てる` を書かせない。
 *   同じ判断が2箇所に増えると、片方が無検査になる。
 */
export const showedFullWageBanner = (blocks: readonly WageAlertBlock[]): boolean =>
  blocks.some((b) => b.kind === "failure" && b.summary === null);
